import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Impersonation tabs (opened via /impersonate#...) must never share localStorage with the admin's
// own tab — otherwise a reload of the admin's tab could rehydrate the merchant session from disk.
// Each browser tab evaluates this module fresh on load, so checking the hash here (synchronously,
// before the store is created) correctly scopes storage per tab without affecting any other tab.
const IMPERSONATION_TAB_FLAG = 'impersonation-tab';

function computeIsImpersonationTab(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.hash.startsWith('#impersonate=')) {
    sessionStorage.setItem(IMPERSONATION_TAB_FLAG, 'true');
    return true;
  }
  return sessionStorage.getItem(IMPERSONATION_TAB_FLAG) === 'true';
}

const isImpersonationTab = computeIsImpersonationTab();

export interface ModuleAccess {
  moduleId?: number;
  moduleCode: string;
  moduleName?: string;
  canView: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canExport?: boolean;
}

export interface User {
  id: number;
  fullName: string;
  email: string;
  adminType: string;
  phone: string;
  role: string;
  status: string;
  company: string;
  department: string;
  userType?: 'ADMIN' | 'MERCHANT';
  merchantId?: number;
  merchantSubUserId?: number;
  moduleAccesses?: ModuleAccess[];
  assignedMerchantIds?: number[];
  /** Parallel to assignedMerchantIds (same order) — merchant names for the referral dropdown. */
  assignedMerchantNames?: string[];
  /** Merchant-only. LIGHTNING shows the Salesforce Classic console; DEFAULT is the previous dashboard. */
  dashboardKey?: 'DEFAULT' | 'LIGHTNING';
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      login: (user, token) => set({ user, token, isAuthenticated: true }),
      logout: () => {
        set({ user: null, token: null, isAuthenticated: false });
        // Clear persisted auth immediately so a hard redirect cannot rehydrate a stale session.
        try {
          localStorage.removeItem('auth-storage');
          sessionStorage.removeItem('impersonation-auth-storage');
        } catch {
          // ignore storage errors (e.g. private browsing)
        }
      },
    }),
    {
      name: isImpersonationTab ? 'impersonation-auth-storage' : 'auth-storage',
      storage: createJSONStorage(() => (isImpersonationTab ? sessionStorage : localStorage)),
    }
  )
);
